import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import ukLocale from '@angular/common/locales/uk';
import { ApplicationConfig, LOCALE_ID, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';

import { AuthEffects, AuthInterceptor, AuthService, authReducer } from '@lib/auth';
import {
  GUARANTEES_HUB_CONFIG,
  GUARANTEES_V2_API_URL,
  GuaranteesHubConfig,
  MEMBER_APPOINTMENTS_API_URL,
  PARTNERS_API_URL,
  PRESCRIPTIONS_API_URL,
  PRESCRIPTION_BASELINES_API_URL,
} from '@lib/guarantees/data-access';
import { medicalDocumentsReducer } from '@lib/medical-documents';
import { memberHubReducer } from '@lib/members/data-access';
import {
  MEMBERS_API_URL,
  fetchMemberActiveCoverageEffect,
  fetchMemberAppointmentsEffect,
  fetchMemberCoverageOptionDiagnosesEffect,
  fetchMemberPackageEffect,
  fetchMemberProfileEffect,
} from '@lib/members/data-access';
import { provideMembersLibConfig } from '@lib/members/feature';
import {
  addPrescriptionDraftEffect,
  clearPrescriptionDraftsEffect,
  deletePrescriptionDraftEffect,
  fetchPrescriptionsEffect,
  loadPrescriptionDraftsFromStorageEffect,
  prescriptionsFeatureReducer,
  prescriptionsPollingEffect,
  processPrescriptionsDraftsEffect,
  refreshPrescriptionsDataEffect,
  resetPrescriptionsStateEffect,
  updatePrescriptionDraftEffect,
} from '@lib/prescriptions/data-access';
import { PHARMACY_ORDER_ENABLED } from '@lib/prescriptions/feature';
import { ADMIN_PORTAL_URL } from '@lib/tasks';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { initializeApp } from 'firebase/app';
import { getMessaging } from 'firebase/messaging';
import { getRemoteConfig } from 'firebase/remote-config';

import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';

import { FeatureFlagService } from '@lib/core/services/feature-flag.service';
import { PortalType, UserProfileResolverService } from '@lib/core/services/user-profile-resolver.service';
import { UserEffects } from '@lib/core/stores/user/user.effects';
import { userReducer } from '@lib/core/stores/user/user.reducer';
import { API_URL, AUTH_API_URL, PROTECTED_API_URL } from '@lib/core/tokens/api.tokens';
import { FIREBASE_API_URL, FIREBASE_CONFIG, FIREBASE_MESSAGING } from '@lib/core/tokens/firebase.tokens';
import { provideTranslateModule } from '@lib/core/translate/translate.provider';

import * as medicalDocumentsEffects from '@lib/medical-documents/core/store/medical-documents.effects';
import { MD_API_URL } from '@lib/medical-documents/core/tokens/medical-documents.tokens';

import { GUARANTEES_V2_ENABLED_OVERRIDE } from '../core/providers/guarantees-v2.config';
import { healthManagerMembersConfig } from '../core/providers/members-lib.config';
import { provideChatLibConfig } from '../core/providers/provide-chat-config.function';
import { environment } from '../environments/environment';
import { routes } from './app.routes';

registerLocaleData(ukLocale);

export const firebaseApp = initializeApp(environment.firebase);
export const messaging = getMessaging(firebaseApp);
const remoteConfig = getRemoteConfig(firebaseApp);
remoteConfig.settings = {
  fetchTimeoutMillis: 0,
  minimumFetchIntervalMillis: 0,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
    provideStore({
      auth: authReducer,
      user: userReducer,
      prescriptions: prescriptionsFeatureReducer,
      memberHub: memberHubReducer,
      medicalDocuments: medicalDocumentsReducer,
    }),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
    provideEffects([AuthEffects, medicalDocumentsEffects, UserEffects]),
    provideEffects({
      loadPrescriptionDraftsFromStorage: loadPrescriptionDraftsFromStorageEffect,
      addPrescriptionDraft: addPrescriptionDraftEffect,
      updatePrescriptionDraft: updatePrescriptionDraftEffect,
      deletePrescriptionDraft: deletePrescriptionDraftEffect,
      clearPrescriptionDrafts: clearPrescriptionDraftsEffect,
      processPrescriptionsDrafts: processPrescriptionsDraftsEffect,
      resetPrescriptionsState: resetPrescriptionsStateEffect,
      fetchPrescriptions: fetchPrescriptionsEffect,
      prescriptionsPolling: prescriptionsPollingEffect,
      refreshPrescriptionsData: refreshPrescriptionsDataEffect,
    }),
    // Use shared member hub effects with correct API_URL injected
    provideEffects({
      fetchMemberProfile: fetchMemberProfileEffect,
      fetchMemberPackage: fetchMemberPackageEffect,
      fetchMemberAppointments: fetchMemberAppointmentsEffect,
      fetchMemberActiveCoverage: fetchMemberActiveCoverageEffect,
      fetchMemberCoverageOptionDiagnoses: fetchMemberCoverageOptionDiagnosesEffect,
    }),
    provideHttpClient(withXhr(), withInterceptors([AuthInterceptor])),
    provideTranslateModule(),
    provideAppInitializer(() => inject(AuthService).initializeTokens()),
    provideAppInitializer(() => inject(FeatureFlagService).initialize()),
    provideAppInitializer(() => inject(UserProfileResolverService).resolveAndSetUserProfile(PortalType.HEALTH_MANAGER)),
    { provide: AUTH_API_URL, useValue: environment.authorizationApiUrl },
    { provide: API_URL, useValue: environment.healthManagerApiUrl },
    { provide: PROTECTED_API_URL, useValue: environment.protectedApiUrl },
    { provide: FIREBASE_API_URL, useValue: environment.healthManagerApiUrl },
    { provide: FIREBASE_CONFIG, useValue: environment.firebase },
    { provide: FIREBASE_MESSAGING, useValue: messaging },
    { provide: MD_API_URL, useValue: environment.protectedApiUrl },
    { provide: ADMIN_PORTAL_URL, useValue: environment.adminPortalUrl },
    { provide: MEMBERS_API_URL, useValue: environment.healthManagerApiUrl },
    { provide: PHARMACY_ORDER_ENABLED, useValue: environment.firebase.projectId !== 'lilo-production' },
    { provide: GUARANTEES_V2_ENABLED_OVERRIDE, useValue: environment.guaranteesV2Enabled ?? null },
    { provide: GUARANTEES_V2_API_URL, useValue: `${environment.protectedApiUrl}/v2` },
    { provide: MEMBER_APPOINTMENTS_API_URL, useValue: `${environment.protectedApiUrl}/members/appointments` },
    { provide: PARTNERS_API_URL, useValue: `${environment.protectedApiUrl}/partners` },
    { provide: PRESCRIPTIONS_API_URL, useValue: `${environment.protectedApiUrl}/prescriptions` },
    { provide: PRESCRIPTION_BASELINES_API_URL, useValue: environment.protectedApiUrl },
    {
      // The hub sits at the api root rather than under the portal's prefix, and the browser cannot
      // set a header on a WebSocket handshake — hence the token factory rather than the interceptor.
      provide: GUARANTEES_HUB_CONFIG,
      useFactory: (): GuaranteesHubConfig => {
        const authService = inject(AuthService);

        return { url: environment.guaranteesHubUrl, accessTokenFactory: () => authService.accessToken() };
      },
    },
    { provide: LOCALE_ID, useValue: 'uk' },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-outlined' },
    },
    provideNativeDateAdapter(),
    provideChatLibConfig(),
    provideMembersLibConfig(healthManagerMembersConfig),
  ],
};
